<script setup lang="ts">
  import dayjs from 'dayjs';
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import { Button } from '@ui/components/button';
  import { HoverCard, HoverCardContent, HoverCardTrigger } from '@ui/components/hover-card';
  import TimeConversionTable from '@ui/components/time/TimeConversionTable.vue';
  import { cn } from '@ui/utils/utils';

  import { defaultTimezone } from './tz';

  interface Props {
    class?: HTMLAttributes['class'];
    dateOnly?: boolean;
    editable?: boolean;
    editIconPosition?: 'left' | 'right';
    fallback?: string;
    format?: string;
    iconClass?: HTMLAttributes['class'];
    local?: boolean;
    openDelay?: number;
    showFromNow?: boolean;
    side?: 'top' | 'right' | 'bottom' | 'left';
    time?: dayjs.Dayjs | null;
    timezone?: string | null; // When undefined, will use default timezone. Use null when you want to default to local timezone
    withConversionTable?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    editIconPosition: 'right',
    fallback: '--',
    format: 'YYYY-MM-DD',
    openDelay: 250,
    side: 'bottom',
    timezone: defaultTimezone,
  });

  const emits = defineEmits<{
    (e: 'click'): void;
  }>();

  const tz = computed<string | undefined>(() => {
    return props.local ? undefined : props.timezone || defaultTimezone || dayjs.tz.guess();
  });

  const computedTime = computed<dayjs.Dayjs | null>(() => {
    if (!props.time) return null;

    return props.timezone === null || props.local ? props.time.local() : props.time.tz(tz.value);
  });

  const formattedDate = computed<string>(() => {
    if (!props.time) return props.fallback;

    if (props.dateOnly) return props.time.format(props.format || 'YYYY-MM-DD');

    const time =
      props.timezone === null || props.local ? props.time.local() : props.time.tz(tz.value);
    const localTime = dayjs.utc().tz(tz.value);

    if (props.format) return time.format(props.format);

    if (localTime.year() !== time.year()) {
      return time.format('MMM D, YYYY h:mm A');
    }

    return time.format('MMM D, h:mm A');
  });

  const currentTime = ref(dayjs().tz(tz.value));

  const timeFromNow = computed(() => {
    if (props.time && currentTime.value.diff(props.time, 'hours') > 8) {
      return formattedDate.value;
    }

    return props.time?.from(currentTime.value);
  });

  const lessThanADayAgo = computed(
    () => props.time && props.time?.diff(currentTime.value, 'day') > -1,
  );

  // update current time every 30 seconds
  let refreshInterval: ReturnType<typeof setInterval> | null = null;
  const updateCurrentTime = () => (currentTime.value = dayjs().tz(tz.value));

  onMounted(() => {
    if (props.showFromNow) {
      refreshInterval = setInterval(updateCurrentTime, 30 * 1000);
    }
  });

  onUnmounted(() => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
  });

  watch(
    computedTime,
    () => {
      updateCurrentTime();
    },
    { immediate: true },
  );
</script>

<template>
  <HoverCard :open-delay="openDelay" :close-delay="0">
    <HoverCardTrigger as-child>
      <slot>
        <Button
          v-if="editable && (time || fallback !== '--')"
          :class="
            cn(
              'flex flex-row gap-1.5 items-center',
              editIconPosition === 'left' && 'flex-row-reverse',
              props.class,
            )
          "
          size="inline"
          variant="ghost"
          @click="emits('click')"
        >
          <span class="inline-block" :title="formattedDate">
            {{ formattedDate }}
          </span>
          <i class="fa fal fa-pencil-alt inline-block" :class="iconClass" />
        </Button>

        <span
          v-else
          :class="cn(withConversionTable && time && 'select-none', props.class)"
          :title="withConversionTable ? '' : formattedDate"
        >
          <template v-if="showFromNow && lessThanADayAgo">
            {{ timeFromNow }}
          </template>
          <template v-else>
            {{ formattedDate || fallback }}
          </template>
        </span>
      </slot>
    </HoverCardTrigger>

    <HoverCardContent v-if="withConversionTable && computedTime" class="w-fit" :side="props.side">
      <TimeConversionTable :time="computedTime" :timezone="tz" />
    </HoverCardContent>
  </HoverCard>
</template>

<style scoped></style>
